import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { cssVar } from '../../../utils/cssVar'
import { useFormattingContext } from '../../organisms/FormattingProvider/FormattingContext'
import { useChartColors } from './ColorProvider'
import { useECharts } from './useECharts'
import { applyFormatters } from './formatters'

const PLATFORM_DEFAULTS = {
  grid: { top: 60, bottom: 60, left: 80, right: 40, containLabel: true },
  tooltip: { show: true }
}

/**
 * One-level-deep merge: platform defaults fill in missing top-level keys,
 * and for object-valued keys both sides are shallow-merged so partial
 * overrides (e.g. `grid: { top: 10 }`) inherit the remaining defaults.
 */
function mergeDefaults (defaults, options) {
  const merged = { ...defaults }
  for (const [key, val] of Object.entries(options)) {
    if (val == null) continue
    const base = defaults[key]
    if (base && typeof base === 'object' && !Array.isArray(base) && typeof val === 'object' && !Array.isArray(val)) {
      merged[key] = { ...base, ...val }
    } else {
      merged[key] = val
    }
  }
  return merged
}

/**
 * InlineChart — renders a complete ECharts configuration with platform theming.
 *
 * Platform defaults (color palette, font, grid, tooltip) are merged underneath
 * the provided options so explicit values always win. The `format` prop injects
 * real JavaScript formatter functions at render time, bridging the restriction
 * that serialized JSX configs cannot contain functions.
 *
 * @example
 * <InlineChart
 *   options={{
 *     xAxis: { type: 'category' },
 *     yAxis: { type: 'value' },
 *     series: [{ type: 'bar', encode: { x: 'month', y: 'revenue' } }]
 *   }}
 *   format={{ yAxis: 'currency', values: 'currency' }}
 *   height={300}
 * />
 */
function InlineChart ({
  options,
  height = 300,
  width,
  className,
  style,
  theme,
  format,
  loading = false,
  loadingConfig,
  onChartReady,
  onEvents,
  autoResize = true,
  applyDefaults = true,
  ...restProps
}) {
  const containerRef = useRef(null)
  const themeColors = useChartColors(theme || 'default')
  const { formatter } = useFormattingContext()

  const mergedOptions = useMemo(() => {
    if (!options) return null

    let opts = options
    if (applyDefaults) {
      opts = mergeDefaults(
        { ...PLATFORM_DEFAULTS, color: themeColors, textStyle: { fontFamily: cssVar('--font-sans') } },
        opts
      )
    }

    return format ? applyFormatters(opts, format, formatter) : opts
  }, [options, themeColors, applyDefaults, format, formatter])

  useECharts(containerRef, mergedOptions, {
    autoResize,
    loading,
    loadingConfig,
    onChartReady,
    onEvents
  })

  return (
    <div
      ref={containerRef}
      className={clsx('echarts-inline-chart', className)}
      style={{ width: width || '100%', height, ...style }}
      {...restProps}
    />
  )
}

InlineChart.propTypes = {
  /** Complete eCharts configuration object. */
  options: PropTypes.object.isRequired,
  /** Chart height (number for px, string for other units). */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Chart width (number for px, string for other units). */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  style: PropTypes.object,
  /**
   * Color theme name ('default', 'firm', 'cool', 'warm', …) or array of
   * palette names. Inherits from a parent ChartColorProvider when absent.
   */
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Format aliases for axis labels and tooltip values, using the platform's
   * FormattingProvider system (numeral.js / dayjs).
   * @example { xAxis: 'date', yAxis: 'currency', values: 'currency' }
   */
  format: PropTypes.shape({
    xAxis: PropTypes.string,
    yAxis: PropTypes.string,
    values: PropTypes.string
  }),
  loading: PropTypes.bool,
  loadingConfig: PropTypes.object,
  /** Called once after the ECharts instance is created. Stable across renders. */
  onChartReady: PropTypes.func,
  /** Event handlers map: `{ click: handler, mouseover: handler }`. */
  onEvents: PropTypes.object,
  autoResize: PropTypes.bool,
  /**
   * When true (default), platform defaults are merged under the provided
   * options. Set to false to pass options through verbatim.
   */
  applyDefaults: PropTypes.bool
}

export default InlineChart
